import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
	Card,
	CardBody,
	CardHeader,
	CardFooter,
	Button,
	Chip,
	Divider,
	Skeleton,
	Checkbox,
	Input,
	Select,
	SelectItem,
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	useDisclosure,
	addToast
} from '@heroui/react';
import {
	Edit,
	Trash2,
	AlertTriangle,
	CheckCircle,
	HelpCircle,
	Save,
	Wrench,
	Gauge,
	Scale,
	ChevronRight,
	Plus,
	Search,
	Plane,
	Pencil,
	Shield,
	MapPin,
	X
} from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';
import { formatDate } from './utils/dateFormatter';
import BaseApi from '../../api/BaseApi.js';
import {
	getAirworthinessStatusFromInspections,
	isInspectionOverdue,
	isInspectionWarning
} from './utils/inspectionStatus';
import { useAccessGroups } from './ResourcesRouter.js';
import DiscrepanciesApi from '../../api/discrepanciesApi.js';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';
import AircraftModelSearch from './components/AircraftModelSearch.js';
import { UnsavedChangesGuard, useUnsavedChangesGuard } from './components/UnsavedChangesGuard.js';
import { fetchResources } from './resources.actions.js';

// Get organization unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

// Pay status options (from php/models/Resources.php)
const PAY_STATUSES = [
	{ value: '', name: 'Not Applicable' },
	{ value: 'perhour', name: 'By Meter' },
	{ value: 'peruse', name: 'Per Use' },
	{ value: 'perblock', name: 'Scheduled Time' }
];

// Medical certificate options (special values + dynamic from server)
const SPECIAL_MEDICAL_OPTIONS = [
	{ key: '', label: 'None' },
	{ key: 'drivingLicense', label: "Driver's License" },
	{ key: 'basicMed', label: 'Basic Med' }
];

// DataRow component for clean label/value display
const DataRow = ({ label, value, children }) => (
	<div>
		<dt className="text-sm text-default-400">{label}</dt>
		<dd className="text-base font-medium mt-0.5">{children || value || '—'}</dd>
	</div>
);

function ViewAircraftPageContent() {
	// Get aircraftId from React Router params
	const { aircraftId: aircraftIdParam } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const accessGroups = useAccessGroups();
	const { setDirty, navigateWithCheck } = useUnsavedChangesGuard();
	const aircraftIdFromState = useSelector(state => state.viewAircraft?.aircraftId);

	// Use aircraftId from params (primary), fallback to state
	const aircraftId = aircraftIdParam ? parseInt(aircraftIdParam, 10) : aircraftIdFromState;

	const [aircraft, setAircraft] = React.useState(null);
	const [inspections, setInspections] = React.useState([]);
	const [discrepancies, setDiscrepancies] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState(null);
	const [isEditing, setIsEditing] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);
	const [editData, setEditData] = React.useState({});
	const [initialCounterData, setInitialCounterData] = React.useState(null);
	const [locations, setLocations] = React.useState([]);
	const [pilotRatings, setPilotRatings] = React.useState([]);
	const [instructorRatings, setInstructorRatings] = React.useState([]);
	const [medicalClass, setMedicalClass] = React.useState([]);
	const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
	const { isOpen: isModelSearchOpen, onOpen: onModelSearchOpen, onClose: onModelSearchClose } = useDisclosure();

	// Fetch dropdown data from API
	React.useEffect(() => {
		const fetchDropdownData = async () => {
			try {
				// Fetch locations
				const locResponse = await ResourcesApi.getLocations();
				setLocations(locResponse.data?.data || locResponse.data || []);

				// Fetch resources data (includes pilotRatings, instructorRatings, medicalClass)
				const resResponse = await ResourcesApi.getResourcesDropdownData();
				const resData = resResponse.data?.data || resResponse.data || {};
				setPilotRatings(resData.pilotRatings || []);
				setInstructorRatings(resData.instructorRatings || []);
				setMedicalClass(resData.medicalClass || []);
			} catch (err) {
				console.error('Failed to load dropdown data:', err);
			}
		};
		fetchDropdownData();
	}, []);

	// Fetch aircraft data
	const fetchAircraftData = React.useCallback(async () => {
		if (!aircraftId) return;
		setLoading(true);
		setError(null);
		try {
			const response = await ResourcesApi.getAircraft(aircraftId);
			const data = response.data?.data || response.data || response;
			setAircraft(data);
		} catch (err) {
			const errorMessage = 'Failed to load aircraft data';
			setError(errorMessage);
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		} finally {
			setLoading(false);
		}
	}, [aircraftId]);

	// Fetch aircraft data on mount
	React.useEffect(() => {
		if (aircraftId) {
			fetchAircraftData();
		}
	}, [aircraftId, fetchAircraftData]);

	// Fetch inspections from Symfony API (computed danger/dueIn fields)
	React.useEffect(() => {
		const fetchInspections = async () => {
			if (!aircraftId) return;
			try {
				const response = await ResourcesApi.getInspections(aircraftId);
				const data = response.data?.data || response.data || [];
				setInspections(Array.isArray(data) ? data : []);
			} catch (err) {
				console.error('Failed to load inspections:', err);
			}
		};
		fetchInspections();
	}, [aircraftId]);

	// Fetch discrepancies from Symfony API
	React.useEffect(() => {
		const fetchDiscrepancies = async () => {
			if (!aircraftId) return;
			try {
				const response = await DiscrepanciesApi.getAircraftDiscrepancies(aircraftId);
				if (response.data?.success && response.data?.data) {
					setDiscrepancies(response.data.data);
				} else if (Array.isArray(response.data)) {
					setDiscrepancies(response.data);
				}
			} catch (err) {
				console.error('Failed to load discrepancies:', err);
			}
		};
		fetchDiscrepancies();
	}, [aircraftId]);

	// Get airworthiness status based on inspections (shared utility, legacy parity)
	const airworthinessStatus = React.useMemo(() => {
		const status = getAirworthinessStatusFromInspections(inspections);
		if (status === 'unairworthy') {
			return { label: 'Unairworthy', color: 'danger', icon: AlertTriangle };
		}
		if (status === 'airworthy') {
			return { label: 'Airworthy', color: 'success', icon: CheckCircle };
		}
		return { label: 'Unknown', color: 'default', icon: HelpCircle };
	}, [inspections]);

	// Count overdue inspections and open discrepancies
	const overdueInspectionsCount = React.useMemo(() => {
		return inspections.filter(isInspectionOverdue).length;
	}, [inspections]);

	const openDiscrepanciesCount = React.useMemo(() => {
		const list = discrepancies.length > 0 ? discrepancies : aircraft?.discrepancies || [];
		return list.filter(d => d.status === 'open').length;
	}, [discrepancies, aircraft]);

	// Navigate to React Router pages
	const handleManageInspections = () => {
		navigateWithCheck(`/aircraft/${aircraft.id}/inspections`);
	};

	const handleViewMeters = () => {
		navigateWithCheck(`/aircraft/${aircraft.id}/meters`);
	};

	const handleManageDiscrepancies = () => {
		navigateWithCheck(`/aircraft/${aircraft.id}/discrepancies`);
	};

	const handleManageWeightBalance = () => {
		navigateWithCheck(`/aircraft/${aircraft.id}/weight-balance`);
	};

	const handleEdit = () => {
		const resource = aircraft.resources?.[0];
		const counters = aircraft.counters || [];
		const hobbsCounter = counters.find(c => c.type === 'hobbs' || c.name?.toLowerCase() === 'hobbs');
		const tachCounters = counters.filter(c => c.type === 'tachometer' || (c.name?.toLowerCase().includes('engine') && c.type !== 'hobbs'));

		// Determine medical certificate value
		let medicalValue = '';
		if (aircraft.drivingLicenseStatus) {
			medicalValue = 'drivingLicense';
		} else if (aircraft.basicMedStatus) {
			medicalValue = 'basicMed';
		} else if (aircraft.medicalGrade?.id) {
			medicalValue = String(aircraft.medicalGrade.id);
		}

		setEditData({
			name: aircraft.name || '',
			registrationNumber: aircraft.registrationNumber || '',
			// Requirements
			pilotRatingId: aircraft.pilotRating?.id ? String(aircraft.pilotRating.id) : '',
			instructorRatingId: aircraft.instructorRating?.id ? String(aircraft.instructorRating.id) : '',
			medicalCertificate: medicalValue,
			// Endorsements
			complex: aircraft.complex ? 1 : 0,
			performance: aircraft.performance ? 1 : 0,
			tailWheel: (aircraft.tailWheel || aircraft.tailwheel) ? 1 : 0,
			highAltitudeOperations: aircraft.highAltitudeOperations ? 1 : 0,
			// Location & Billing
			locationId: resource?.location?.id ? String(resource.location.id) : '',
			payStatus: resource?.payStatus || '',
			rate: resource?.rate || '',
			// Meters
			enableHobbs: !!aircraft.enableHobbs,
			hobbsId: hobbsCounter?.id || null,
			hobbsValue: hobbsCounter?.value || '',
			hobbsEnableRate: !!hobbsCounter?.rate,
			hobbsRate: hobbsCounter?.rate || '',
			enableTachometers: !!aircraft.enableTachometers,
			engineCounters: tachCounters.map((c, i) => ({
				id: c.id,
				name: c.name || `Engine ${i + 1}`,
				value: c.value || '',
				enableRate: !!c.rate,
				rate: c.rate || ''
			}))
		});
		setInitialCounterData({
			enableHobbs: !!aircraft.enableHobbs,
			hobbsValue: hobbsCounter?.value || '',
			hobbsEnableRate: !!hobbsCounter?.rate,
			hobbsRate: hobbsCounter?.rate || '',
			enableTachometers: !!aircraft.enableTachometers,
			engineCounters: tachCounters.map((c, i) => ({
				id: c.id,
				name: c.name || `Engine ${i + 1}`,
				value: c.value || '',
				enableRate: !!c.rate,
				rate: c.rate || ''
			}))
		});
		setIsEditing(true);
	};

	const handleCancelEdit = () => {
		setDirty(false);
		setIsEditing(false);
		setEditData({});
		setInitialCounterData(null);
	};

	const handleSave = async () => {
		if (!editData.locationId) {
			addToast({
				title: 'Error',
				description: 'Location is required',
				color: 'danger'
			});
			return;
		}

		setIsSaving(true);
		try {
			// Parse medical certificate
			const medicalCert = editData.medicalCertificate;
			const isDrivingLicense = medicalCert === 'drivingLicense';
			const isBasicMed = medicalCert === 'basicMed';
			const medicalCertificateId = (!isDrivingLicense && !isBasicMed && medicalCert)
				? parseInt(medicalCert, 10)
				: null;

			// Symfony API payload
			const payload = {
				name: editData.name,
				registrationNumber: editData.registrationNumber,
				// Model (if changed)
				...(editData.modelId && { modelId: editData.modelId }),
				// Requirements
				pilotRatingId: editData.pilotRatingId ? parseInt(editData.pilotRatingId, 10) : null,
				instructorRatingId: editData.instructorRatingId ? parseInt(editData.instructorRatingId, 10) : null,
				// Medical
				drivingLicense: isDrivingLicense,
				basicMedStatus: isBasicMed,
				medicalCertificateId: medicalCertificateId,
				// Endorsements
				endorsements: {
					complex: !!editData.complex,
					performance: !!editData.performance,
					tailwheel: !!editData.tailWheel,
					highAltitudeOperations: !!editData.highAltitudeOperations
				},
				// Location & Billing
				resourceLocationId: editData.locationId ? parseInt(editData.locationId, 10) : null,
				resourcePayStatus: editData.payStatus || null,
				resourceRate: editData.rate != null && editData.rate !== '' ? String(editData.rate) : null,
				// Meters — only include counters if user changed meter configuration
				enableHobbs: editData.enableHobbs,
				enableTachometers: editData.enableTachometers
			};

			// Only include counters in payload if user actually changed meter configuration
			const hasCounterChanges = () => {
				if (!initialCounterData) return false;
				if (editData.enableHobbs !== initialCounterData.enableHobbs) return true;
				if (editData.enableTachometers !== initialCounterData.enableTachometers) return true;
				if (String(editData.hobbsValue) !== String(initialCounterData.hobbsValue)) return true;
				if (String(editData.hobbsRate) !== String(initialCounterData.hobbsRate)) return true;
				if (!!editData.hobbsEnableRate !== !!initialCounterData.hobbsEnableRate) return true;
				const curr = editData.engineCounters || [];
				const init = initialCounterData.engineCounters || [];
				if (curr.length !== init.length) return true;
				for (let i = 0; i < curr.length; i++) {
					if (String(curr[i].value) !== String(init[i].value)) return true;
					if (String(curr[i].rate || '') !== String(init[i].rate || '')) return true;
					if (!!curr[i].enableRate !== !!init[i].enableRate) return true;
					if (curr[i].name !== init[i].name) return true;
				}
				return false;
			};

			if (hasCounterChanges()) {
				const counters = [];
				(editData.engineCounters || []).forEach(engine => {
					counters.push({
						id: engine.id,
						name: engine.name,
						value: String(engine.value || '0'),
						type: 'tachometer',
						enableRate: engine.enableRate,
						rate: engine.rate ? String(engine.rate) : null
					});
				});
				if (editData.enableHobbs) {
					counters.push({
						...(editData.hobbsId && { id: editData.hobbsId }),
						name: 'Hobbs',
						value: String(editData.hobbsValue || '0'),
						type: 'hobbs',
						enableRate: editData.hobbsEnableRate,
						rate: editData.hobbsRate ? String(editData.hobbsRate) : null
					});
				}
				payload.counters = counters;
			}

			await ResourcesApi.updateAircraft(aircraft.id, payload);
			addToast({
				title: 'Success',
				description: 'Aircraft updated successfully',
				color: 'success'
			});
			setDirty(false);
			setIsEditing(false);
			setInitialCounterData(null);
			fetchAircraftData();
		} catch (err) {
			const errorMessage =
				err.response?.data?.error?.[0] ||
				err.response?.data?.message ||
				err.message ||
				'Failed to update aircraft';
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		} finally {
			setIsSaving(false);
		}
	};

	const handleEditChange = (field, value) => {
		if (editData[field] === value) return;

		setEditData(prev => ({ ...prev, [field]: value }));
		setDirty(true);
	};

	// Handle engine counter changes
	const handleEngineChange = (index, field, value) => {
		setEditData(prev => {
			const updated = [...(prev.engineCounters || [])];
			updated[index] = { ...updated[index], [field]: value };
			return { ...prev, engineCounters: updated };
		});
		setDirty(true);
	};

	// Add new engine counter
	const handleAddEngine = () => {
		setEditData(prev => {
			const engines = prev.engineCounters || [];
			return {
				...prev,
				engineCounters: [
					...engines,
					{
						name: `Engine ${engines.length + 1}`,
						value: '',
						enableRate: false,
						rate: ''
					}
				]
			};
		});
		setDirty(true);
	};

	// Remove engine counter
	const handleRemoveEngine = index => {
		setEditData(prev => {
			const engines = (prev.engineCounters || []).filter((_, i) => i !== index);
			// Rename remaining engines
			return {
				...prev,
				engineCounters: engines.map((engine, i) => ({
					...engine,
					name: engine.id ? engine.name : `Engine ${i + 1}`
				}))
			};
		});
		setDirty(true);
	};

	// Model search handler (called from AircraftModelSearch component)
	const handleSelectModel = model => {
		setEditData(prev => ({
			...prev,
			modelId: model.id,
			selectedModel: model
		}));
		setDirty(true);
		onModelSearchClose();
	};

	const handleDelete = async () => {
		try {
			// Use resource ID to delete - this marks both resource AND aircraft as trashed
			const resourceId = aircraft.resources?.[0]?.id;
			if (!resourceId) {
				throw new Error('Resource not found for this aircraft');
			}
			await ResourcesApi.deleteResource(resourceId);
			dispatch(fetchResources());
			addToast({
				title: 'Success',
				description: 'Aircraft deleted successfully',
				color: 'success'
			});
			onDeleteClose();
			navigate('/');
		} catch (err) {
			const errorMessage =
				err.response?.data?.error?.[0] ||
				err.response?.data?.message ||
				err.message ||
				'Failed to delete aircraft';
			addToast({
				title: 'Cannot Delete',
				description: errorMessage,
				color: 'danger'
			});
		}
	};

	// Format rate display (matches legacy: "By Meter", "$X/Use", "$X/Scheduled Time")
	const formatRateDisplay = () => {
		const resource = aircraft?.resources?.[0];
		if (!resource?.payStatus) return '—';
		if (resource.payStatus === 'perhour') return 'By Meter';
		if (resource.payStatus === 'peruse') return resource.rate ? `$${resource.rate}/Use` : 'Per Use';
		if (resource.payStatus === 'perblock') return resource.rate ? `$${resource.rate}/Scheduled Time` : 'Scheduled Time';
		return '—';
	};

	const orgUnique = getOrgUnique();
	const isHangars = window.location.pathname.includes('/hangars');
	const breadcrumbCustomItems = [{ label: isHangars ? 'Aircraft Status' : 'Resources', href: `/org/${orgUnique}/${isHangars ? 'hangars' : 'resources'}/` }];

	const handleBackToResources = () => {
		navigateWithCheck('/');
	};

	// Loading skeleton - 3-column layout
	const renderSkeleton = () => (
		<>
			{/* Header skeleton */}
			<div className="mb-8">
				<Skeleton className="w-32 h-8 rounded-lg mb-6" />
				{/* Same col→row split as the real header below it — the fixed-width
				    skeletons total ~456px and jolted a 390px viewport sideways on
				    every fetch. */}
				<div className="flex flex-col md:flex-row justify-between items-start gap-4">
					<div>
						<Skeleton className="w-64 max-w-full h-9 rounded-lg mb-2" />
						<Skeleton className="w-48 max-w-full h-5 rounded-lg" />
					</div>
					<div className="flex gap-2">
						<Skeleton className="w-20 h-10 rounded-lg" />
						<Skeleton className="w-28 h-10 rounded-lg" />
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
				{/* Column 1: Aircraft Model skeleton */}
				<div className="space-y-4">
					<Skeleton className="w-full h-72 rounded-xl" />
				</div>

				{/* Column 2: Aircraft Settings skeleton */}
				<div className="space-y-4">
					<Skeleton className="w-full h-96 rounded-xl" />
				</div>

				{/* Column 3: Operations skeleton */}
				<div className="space-y-4">
					{[1, 2, 3, 4].map(i => (
						<Skeleton key={i} className="w-full h-32 rounded-xl" />
					))}
				</div>
			</div>
		</>
	);

	// Error state
	const renderError = () => (
		<div className="text-center py-16">
			<AlertTriangle className="w-16 h-16 text-danger mx-auto mb-4" />
			<p className="text-danger text-lg mb-4">{error}</p>
			<Button color="primary" variant="flat" onPress={fetchAircraftData}>
				Try Again
			</Button>
		</div>
	);

	// Main content - HeroUI style
	const renderContent = () => {
		if (!aircraft) return null;

		const counters = aircraft.counters || [];
		const discrepanciesList =
			discrepancies.length > 0 ? discrepancies : aircraft.discrepancies || [];

		return (
			<>
				{/* Page Header */}
				<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
					<div>
						<div className="flex items-center gap-3 mb-1">
							<h1 className="text-2xl font-bold">{aircraft.name}</h1>
							<Chip
								color={airworthinessStatus.color}
								variant="flat"
								size="sm"
								startContent={
									<airworthinessStatus.icon className="w-3 h-3" />
								}
							>
								{airworthinessStatus.label}
							</Chip>
						</div>
						<p className="text-default-500 text-sm">
							{aircraft.registrationNumber}
							{(aircraft.manufacturer || aircraft.model) && ' · '}
							{[aircraft.manufacturer, aircraft.model].filter(Boolean).join(' ')}
						</p>
					</div>

					{/* Action Buttons + Close */}
					<div className="flex flex-wrap items-center gap-3 self-end md:self-auto">
						{isEditing ? (
							<>
								<Button
									variant="bordered"
									onPress={handleCancelEdit}
									isDisabled={isSaving}
								>
									Cancel
								</Button>
								<Button
									color="primary"
									startContent={<Save className="w-4 h-4" />}
									onPress={handleSave}
									isLoading={isSaving}
									isDisabled={!editData.locationId}
								>
									Save Changes
								</Button>
							</>
						) : accessGroups.resourcesManage ? (
							<>
								<Button
									color="danger"
									variant="light"
									startContent={<Trash2 className="w-4 h-4" />}
									onPress={onDeleteOpen}
								>
									Delete
								</Button>
								<Button
									variant="bordered"
									startContent={<Edit className="w-4 h-4" />}
									onPress={handleEdit}
								>
									Edit
								</Button>
							</>
						) : null}
						<Button variant="light" isIconOnly onPress={handleBackToResources}>
							<X className="w-5 h-5" />
						</Button>
					</div>
				</div>

				{/* Main Grid - 3 Equal Columns */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
					{/* Column 1: Aircraft Model (READ-ONLY) */}
					<div className="space-y-4">
						<Card className="border border-default-200 shadow-md bg-gradient-to-br from-white to-sky-50/30">
							<CardHeader className="bg-gradient-to-r from-sky-100/80 via-sky-50/50 to-transparent pb-3 border-b border-sky-100/50">
								<div className="flex items-center justify-between w-full">
									<div className="flex items-center gap-3">
										<div className="p-2 rounded-lg bg-sky-500/10 ring-1 ring-sky-500/20">
											<Plane className="w-5 h-5 text-sky-600" />
										</div>
										<span className="text-lg font-semibold text-sky-900">Aircraft Model</span>
									</div>
									{isEditing && (
										<Button
											size="sm"
											variant="flat"
											color="primary"
											startContent={<Search className="w-3 h-3" />}
											onPress={onModelSearchOpen}
											className="bg-sky-500/10 text-sky-700 hover:bg-sky-500/20"
										>
											Change
										</Button>
									)}
								</div>
							</CardHeader>
							<CardBody className="pt-4">
								{/* Model Info - READ-ONLY display */}
								{isEditing && editData.selectedModel ? (
									<div className="space-y-3">
										{[
											{ label: 'Manufacturer', value: editData.selectedModel.manufacturer },
											{ label: 'Model', value: editData.selectedModel.model },
											editData.selectedModel.popularName && { label: 'Popular Name', value: editData.selectedModel.popularName },
											{ label: 'Type', value: editData.selectedModel.typeAircraft },
											{ label: 'Engine', value: editData.selectedModel.typeEngine },
											{ label: 'Engines', value: editData.selectedModel.numberEngine },
											{ label: 'Seats', value: editData.selectedModel.numberSeats }
										].filter(Boolean).map((item, idx) => (
											<div key={idx} className="flex justify-between items-center py-2 border-b border-default-100 last:border-0">
												<span className="text-sm text-default-500">{item.label}</span>
												<span className="text-sm font-medium text-default-800">{item.value || '—'}</span>
											</div>
										))}
									</div>
								) : (
									<div className="space-y-3">
										{[
											{ label: 'Manufacturer', value: aircraft.manufacturer || aircraft.manufacturerName },
											{ label: 'Model', value: aircraft.model },
											aircraft.popularName && { label: 'Popular Name', value: aircraft.popularName },
											{ label: 'Type', value: aircraft.aircraftType },
											{ label: 'Category', value: aircraft.aircraftCategory },
											{ label: 'Engine Type', value: aircraft.engineType },
											{ label: 'Engines', value: aircraft.engineNumber },
											{ label: 'Seats', value: aircraft.seatsNumber || aircraft.seatsCount },
											aircraft.speed && aircraft.speed !== '0000' && { label: 'Speed', value: aircraft.speed }
										].filter(Boolean).map((item, idx) => (
											<div key={idx} className="flex justify-between items-center py-2 border-b border-default-100 last:border-0">
												<span className="text-sm text-default-500">{item.label}</span>
												<span className="text-sm font-medium text-default-800">{item.value || '—'}</span>
											</div>
										))}
									</div>
								)}
							</CardBody>
						</Card>
					</div>

					{/* Column 2: Aircraft Settings (EDITABLE) */}
					<div className="space-y-4">
						<Card className="border border-default-200 shadow-md bg-gradient-to-br from-white to-emerald-50/30">
							<CardHeader className="bg-gradient-to-r from-emerald-100/80 via-emerald-50/50 to-transparent pb-3 border-b border-emerald-100/50">
								<div className="flex items-center gap-3">
									<div className="p-2 rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/20">
										<Pencil className="w-5 h-5 text-emerald-600" />
									</div>
									<span className="text-lg font-semibold text-emerald-900">Aircraft Settings</span>
								</div>
							</CardHeader>
							{/* overflow-x-clip: the body scrolls vertically, so a single stray
							    pixel of width paints a horizontal bar (owner report) — clip it;
							    the wrap fixes above keep real content visible. */}
							<CardBody className="pt-4 space-y-5 overflow-x-clip">
								{/* Section: Identification */}
								<div>
									<div className="mb-5">
										<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
											<span className="w-1 h-4 bg-emerald-400 rounded-full" />
											Identification
										</h4>
									</div>
									{isEditing ? (
										<div className="space-y-3">
											<Input
												label="Aircraft Name"
												value={editData.name}
												onChange={e => handleEditChange('name', e.target.value)}
												placeholder="Enter aircraft name"
												size="sm"
												variant="bordered"
												classNames={{ inputWrapper: 'border border-emerald-200 hover:border-emerald-400 focus-within:!border-emerald-500' }}
											/>
											<Input
												label="Registration Number"
												value={editData.registrationNumber}
												onChange={e => handleEditChange('registrationNumber', e.target.value)}
												placeholder="N12345"
												size="sm"
												variant="bordered"
												classNames={{ inputWrapper: 'border border-emerald-200 hover:border-emerald-400 focus-within:!border-emerald-500' }}
											/>
										</div>
									) : (
										<div className="space-y-2">
											<div className="flex justify-between items-center gap-3 py-2 border-b border-default-100">
												<span className="text-sm text-default-500 shrink-0">Name</span>
												<span className="text-sm font-medium min-w-0 text-right break-words">{aircraft.name || '—'}</span>
											</div>
											<div className="flex justify-between items-center gap-3 py-2">
												<span className="text-sm text-default-500 shrink-0">Registration</span>
												<span className="text-sm font-medium font-mono min-w-0 text-right break-words">{aircraft.registrationNumber || '—'}</span>
											</div>
										</div>
									)}
								</div>

								<Divider className="mt-4 bg-emerald-100" />

								{/* Section: Requirements */}
								<div>
									<div className="mb-5">
										<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
											<span className="w-1 h-4 bg-amber-400 rounded-full" />
											Requirements
										</h4>
									</div>
									{isEditing ? (
										<div className="space-y-3">
											<Select
												label="Pilot Rating"
												size="sm"
												aria-label="Pilot Rating"
												placeholder="Select pilot rating"
												selectedKeys={editData.pilotRatingId && pilotRatings.some(r => String(r.id) === editData.pilotRatingId) ? [editData.pilotRatingId] : []}
												onSelectionChange={keys =>
													handleEditChange('pilotRatingId', Array.from(keys)[0] || '')
												}
												variant="bordered"
												classNames={{ trigger: 'border border-amber-200 hover:border-amber-400 data-[focus=true]:border-amber-500' }}
											>
												{pilotRatings.map(rating => (
													<SelectItem key={String(rating.id)}>{rating.name}</SelectItem>
												))}
											</Select>
											<Select
												label="Instructor Rating"
												size="sm"
												aria-label="Instructor Rating"
												placeholder="Select instructor rating"
												selectedKeys={editData.instructorRatingId && instructorRatings.some(r => String(r.id) === editData.instructorRatingId) ? [editData.instructorRatingId] : []}
												onSelectionChange={keys =>
													handleEditChange('instructorRatingId', Array.from(keys)[0] || '')
												}
												variant="bordered"
												classNames={{ trigger: 'border border-amber-200 hover:border-amber-400 data-[focus=true]:border-amber-500' }}
											>
												{instructorRatings.map(rating => (
													<SelectItem key={String(rating.id)}>{rating.name}</SelectItem>
												))}
											</Select>
											<Select
												label="Medical Certificate"
												size="sm"
												aria-label="Medical Certificate"
												placeholder="None"
												selectedKeys={editData.medicalCertificate && (
												SPECIAL_MEDICAL_OPTIONS.some(o => o.key === editData.medicalCertificate) ||
												medicalClass.some(c => String(c.id) === editData.medicalCertificate)
											) ? [editData.medicalCertificate] : ['']}
												onSelectionChange={keys =>
													handleEditChange('medicalCertificate', Array.from(keys)[0] || '')
												}
												variant="bordered"
												classNames={{ trigger: 'border border-amber-200 hover:border-amber-400 data-[focus=true]:border-amber-500' }}
											>
												{SPECIAL_MEDICAL_OPTIONS.map(opt => (
													<SelectItem key={opt.key}>{opt.label}</SelectItem>
												))}
												{medicalClass.map(cls => (
													<SelectItem key={String(cls.id)}>{cls.name}</SelectItem>
												))}
											</Select>
										</div>
									) : (
										<div className="space-y-2">
											<div className="flex justify-between items-center gap-3 py-2 border-b border-default-100">
												<span className="text-sm text-default-500 shrink-0">Pilot Rating</span>
												<span className="text-sm font-medium min-w-0 text-right break-words">{aircraft.pilotRating?.name || '—'}</span>
											</div>
											<div className="flex justify-between items-center gap-3 py-2 border-b border-default-100">
												<span className="text-sm text-default-500 shrink-0">Instructor Rating</span>
												<span className="text-sm font-medium min-w-0 text-right break-words">{aircraft.instructorRating?.name || '—'}</span>
											</div>
											<div className="flex justify-between items-center gap-3 py-2">
												<span className="text-sm text-default-500 shrink-0">Medical Certificate</span>
												<span className="text-sm font-medium min-w-0 text-right break-words">
													{aircraft.drivingLicenseStatus ? "Driver's License" :
													aircraft.basicMedStatus ? 'Basic Med' :
													aircraft.medicalGrade?.name || '—'}
												</span>
											</div>
										</div>
									)}
								</div>

								<Divider className="mt-4 bg-emerald-100" />

								{/* Section: Endorsements */}
								<div>
									<div className="mb-5">
										<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
											<span className="w-1 h-4 bg-violet-400 rounded-full" />
											Endorsements
										</h4>
									</div>
									<div className="grid grid-cols-2 gap-3">
										<Checkbox
											isSelected={isEditing ? !!editData.complex : !!aircraft.complex}
											isDisabled={!isEditing}
											isReadOnly={!isEditing}
											onChange={e => handleEditChange('complex', e.target.checked ? 1 : 0)}
											size="sm"
											classNames={{ wrapper: 'before:border-violet-300 after:bg-violet-500', label: 'font-normal' }}
										>
											<span className="text-sm">Complex</span>
										</Checkbox>
										<Checkbox
											isSelected={isEditing ? !!editData.performance : !!aircraft.performance}
											isDisabled={!isEditing}
											isReadOnly={!isEditing}
											onChange={e => handleEditChange('performance', e.target.checked ? 1 : 0)}
											size="sm"
											classNames={{ wrapper: 'before:border-violet-300 after:bg-violet-500', label: 'font-normal' }}
										>
											<span className="text-sm">High Performance</span>
										</Checkbox>
										<Checkbox
											isSelected={isEditing ? !!editData.tailWheel : !!(aircraft.tailWheel || aircraft.tailwheel)}
											isDisabled={!isEditing}
											isReadOnly={!isEditing}
											onChange={e => handleEditChange('tailWheel', e.target.checked ? 1 : 0)}
											size="sm"
											classNames={{ wrapper: 'before:border-violet-300 after:bg-violet-500', label: 'font-normal' }}
										>
											<span className="text-sm">Tailwheel</span>
										</Checkbox>
										<Checkbox
											isSelected={
												isEditing
													? !!editData.highAltitudeOperations
													: !!aircraft.highAltitudeOperations
											}
											isDisabled={!isEditing}
											isReadOnly={!isEditing}
											onChange={e =>
												handleEditChange('highAltitudeOperations', e.target.checked ? 1 : 0)
											}
											size="sm"
											classNames={{ wrapper: 'before:border-violet-300 after:bg-violet-500', label: 'font-normal' }}
										>
											<span className="text-sm">High Altitude</span>
										</Checkbox>
									</div>
								</div>

								<Divider className="mt-4 bg-emerald-100" />

								{/* Section: Location & Billing */}
								<div>
									<div className="mb-5">
										<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
											<span className="w-1 h-4 bg-blue-400 rounded-full" />
											Location & Billing
										</h4>
									</div>
									{isEditing ? (
										<div className="space-y-3">
											<Select
												label="Location"
												size="sm"
												aria-label="Location"
												placeholder="Select location"
												isRequired
												disallowEmptySelection
												isInvalid={!editData.locationId}
												errorMessage="Location is required"
												selectedKeys={editData.locationId ? [editData.locationId] : []}
												onSelectionChange={keys =>
													handleEditChange('locationId', Array.from(keys)[0] || editData.locationId || '')
												}
												variant="bordered"
												classNames={{ trigger: 'border border-blue-200 hover:border-blue-400 data-[focus=true]:border-blue-500' }}
											>
												{locations.map(loc => (
													<SelectItem key={String(loc.id)}>
														{loc.city ? `${loc.name}, ${loc.city}` : loc.name}
													</SelectItem>
												))}
											</Select>
											<Select
												label="Charge Type"
												size="sm"
												aria-label="Charge Type"
												placeholder="Not Applicable"
												selectedKeys={editData.payStatus ? [editData.payStatus] : ['']}
												onSelectionChange={keys =>
													handleEditChange('payStatus', Array.from(keys)[0] || '')
												}
												variant="bordered"
												classNames={{ trigger: 'border border-blue-200 hover:border-blue-400 data-[focus=true]:border-blue-500' }}
											>
												{PAY_STATUSES.map(status => (
													<SelectItem key={status.value}>{status.name}</SelectItem>
												))}
											</Select>
											{(editData.payStatus === 'peruse' || editData.payStatus === 'perblock') && (
												<Input
													label="Rate"
													size="sm"
													type="number"
													value={editData.rate}
													onChange={e => handleEditChange('rate', e.target.value)}
													placeholder="0.00"
													startContent={<span className="text-default-400 text-sm">$</span>}
													endContent={<span className="text-default-400 text-sm">/hr</span>}
													variant="bordered"
													classNames={{ inputWrapper: 'border border-blue-200 hover:border-blue-400 focus-within:!border-blue-500' }}
												/>
											)}
										</div>
									) : (
										<div className="space-y-2">
											<div className="flex justify-between items-center gap-3 py-2 border-b border-default-100">
												<span className="text-sm text-default-500 shrink-0">Location</span>
												<span className="text-sm font-medium min-w-0 text-right break-words">
													{aircraft.resources?.[0]?.location?.name ||
													aircraft.location?.name ||
													'—'}
												</span>
											</div>
											<div className="flex justify-between items-center gap-3 py-2">
												<span className="text-sm text-default-500 shrink-0">Charge Type</span>
												<span className="text-sm font-medium min-w-0 text-right break-words">{formatRateDisplay()}</span>
											</div>
										</div>
									)}
								</div>
							</CardBody>
						</Card>
					</div>

					{/* Column 3: Operations (Links + Editable Meters) */}
					<div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
						{/* Meters Card - EDITABLE (meterView permission) */}
						{(accessGroups._loaded && accessGroups.meterView) && (
						<Card className="border border-default-200 shadow-md bg-gradient-to-br from-white to-cyan-50/30">
							<CardHeader className="bg-gradient-to-r from-cyan-100/80 via-cyan-50/50 to-transparent pb-3 border-b border-cyan-100/50">
								<div className="flex items-center justify-between w-full">
									<div className="flex items-center gap-3">
										<div className="p-2 rounded-lg bg-cyan-500/10 ring-1 ring-cyan-500/20">
											<Gauge className="w-5 h-5 text-cyan-600" />
										</div>
										<span className="text-lg font-semibold text-cyan-900">Meters</span>
									</div>
									{isEditing && (
										<Button
											size="sm"
											variant="flat"
											color="success"
											startContent={<Plus className="w-3 h-3" />}
											onPress={handleAddEngine}
											className="bg-cyan-500/10 text-cyan-700 hover:bg-cyan-500/20"
										>
											Add
										</Button>
									)}
								</div>
							</CardHeader>
							<CardBody className="pt-4 pb-2">
								{isEditing ? (
									<div className="space-y-3">
										{/* Hobbs */}
										<div className="flex items-center justify-between p-2 rounded-lg bg-default-50">
											<Checkbox
												isSelected={editData.enableHobbs}
												onValueChange={value => handleEditChange('enableHobbs', value)}
												size="sm"
												classNames={{ wrapper: 'before:border-cyan-300 after:bg-cyan-500' }}
											>
												<span className="text-sm font-medium min-w-0 text-right break-words">Hobbs</span>
											</Checkbox>
											{editData.enableHobbs && (
												<Input
													placeholder="0"
													value={editData.hobbsValue}
													onValueChange={value => handleEditChange('hobbsValue', value)}
													type="text"
													size="sm"
													className="w-24"
													variant="bordered"
													classNames={{ input: 'text-right font-mono', inputWrapper: 'border border-cyan-200' }}
												/>
											)}
										</div>
										{editData.enableHobbs && editData.payStatus !== 'peruse' && (
											<div className="flex items-center gap-2 pl-6 text-xs">
												<Checkbox
													isSelected={editData.hobbsEnableRate}
													onValueChange={value => handleEditChange('hobbsEnableRate', value)}
													size="sm"
												>
													Rate
												</Checkbox>
												{editData.hobbsEnableRate && (
													<Input
														placeholder="0.00"
														value={editData.hobbsRate}
														onValueChange={value => handleEditChange('hobbsRate', value)}
														type="text"
														size="sm"
														className="w-20"
														startContent={<span className="text-xs text-default-400">$</span>}
														variant="bordered"
														classNames={{ input: 'text-right text-xs font-mono' }}
													/>
												)}
											</div>
										)}

										{/* Tachometers */}
										<Divider className="my-2 bg-cyan-100" />
										<div className="p-2 rounded-lg bg-default-50">
											<Checkbox
												isSelected={editData.enableTachometers}
												onValueChange={value => handleEditChange('enableTachometers', value)}
												size="sm"
												classNames={{ wrapper: 'before:border-cyan-300 after:bg-cyan-500' }}
											>
												<span className="text-sm font-medium min-w-0 text-right break-words">Tachometers</span>
											</Checkbox>
										</div>

										{/* Engine counters */}
										{(editData.engineCounters || []).map((engine, index) => (
											<div key={index} className="pl-3 border-l-2 border-cyan-200 space-y-2 ml-2">
												<div className="flex items-center gap-2">
													<Button
														isIconOnly
														size="sm"
														variant="light"
														color="danger"
														className="min-w-6 w-6 h-6"
														onPress={() => handleRemoveEngine(index)}
													>
														<Trash2 className="w-3 h-3" />
													</Button>
													<span className="text-sm font-medium flex-1">{engine.name}</span>
													<Input
														placeholder="0"
														value={engine.value}
														onValueChange={value => handleEngineChange(index, 'value', value)}
														type="text"
														size="sm"
														className="w-24"
														variant="bordered"
														classNames={{ input: 'text-right font-mono', inputWrapper: 'border border-cyan-200' }}
													/>
												</div>
												{editData.payStatus !== 'peruse' && (
													<div className="flex items-center gap-2 pl-8 text-xs">
														<Checkbox
															isSelected={engine.enableRate}
															onValueChange={value => handleEngineChange(index, 'enableRate', value)}
															size="sm"
														>
															Rate
														</Checkbox>
														{engine.enableRate && (
															<Input
																placeholder="0.00"
																value={engine.rate}
																onValueChange={value => handleEngineChange(index, 'rate', value)}
																type="text"
																size="sm"
																className="w-20"
																startContent={<span className="text-xs text-default-400">$</span>}
																variant="bordered"
																classNames={{ input: 'text-right text-xs font-mono' }}
															/>
														)}
													</div>
												)}
											</div>
										))}

										{(editData.engineCounters || []).length === 0 && editData.enableTachometers && (
											<p className="text-xs text-default-400 pl-6 italic">Click "Add" to add engine</p>
										)}
									</div>
								) : (
									<>
										{counters.length > 0 ? (
											<div className="space-y-2">
												{counters.slice(0, 4).map((counter, index) => (
													<div
														key={counter.id || `counter-${index}`}
														className="flex justify-between items-center text-sm py-2 border-b border-default-100 last:border-0"
													>
														<span className="text-default-500">{counter.name}</span>
														<span className="font-mono font-semibold text-cyan-700">{counter.value || 0}</span>
													</div>
												))}
											</div>
										) : (
											<p className="text-default-400 text-sm py-2 italic">No meters configured</p>
										)}
									</>
								)}
							</CardBody>
							<CardFooter className="pt-2 border-t border-cyan-100/50">
								<Button
									variant="light"
									size="sm"
									className="w-full text-cyan-600 hover:bg-cyan-50"
									endContent={<ChevronRight className="w-4 h-4" />}
									onPress={handleViewMeters}
								>
									{accessGroups._loaded && !accessGroups.resourcesHistory ? 'View Meters' : 'View History'}
								</Button>
							</CardFooter>
						</Card>
						)}

						{/* Inspections Card (inspectionView permission) */}
						{(accessGroups._loaded && accessGroups.inspectionView) && (
						<Card className="border border-default-200 shadow-sm bg-gradient-to-br from-white to-amber-50/20 hover:shadow-md transition-shadow">
							<CardHeader className="bg-gradient-to-r from-amber-100/60 via-amber-50/40 to-transparent pb-2 border-b border-amber-100/30">
								<div className="flex items-center justify-between w-full">
									<div className="flex items-center gap-3">
										<div className="p-1.5 rounded-lg bg-amber-500/10 ring-1 ring-amber-500/20">
											<Wrench className="w-4 h-4 text-amber-600" />
										</div>
										<span className="text-base font-semibold text-amber-900">Inspections</span>
									</div>
									{overdueInspectionsCount > 0 && (
										<Chip size="sm" color="danger" variant="flat" className="animate-pulse">
											{overdueInspectionsCount} overdue
										</Chip>
									)}
								</div>
							</CardHeader>
							<CardBody className="pt-3 pb-2">
								{inspections.length > 0 ? (
									// overflow-x too: nowrap date/dueIn columns can exceed the
									// ~326px card body on a phone, and relying on overflow-y's
									// implicit x-auto desyncs the sticky header.
									<div className="max-h-52 overflow-y-auto overflow-x-auto">
										<table className="w-full text-xs">
											<thead className="sticky top-0 bg-white z-10">
												<tr className="text-default-400 border-b border-default-100">
													<th data-key="name" className="text-left py-2 pr-2 font-medium text-[11px]">Name</th>
													<th data-key="due" className="text-left py-2 pr-2 font-medium text-[11px]">Due Date/Meter</th>
													<th data-key="left" className="text-right py-2 font-medium text-[11px]">Time Left</th>
												</tr>
											</thead>
											<tbody>
												{inspections.slice(0, 4).map((inspection, index) => {
													const isDanger = isInspectionOverdue(inspection);
													const isWarning = isInspectionWarning(inspection);
													const displayName =
														inspection.inspectionName ||
														inspection.name ||
														inspection.unique ||
														inspection.type ||
														'-';
													const dueDateMeter = inspection.dueDate
														? formatDate(inspection.dueDate)
														: inspection.counterExpirationValue
															? String(inspection.counterExpirationValue)
															: '—';
													const rowBg = isDanger
														? 'bg-danger-50 text-danger-600'
														: isWarning
															? 'bg-warning-50 text-warning-600'
															: '';
													return (
														<tr
															key={inspection.id || `inspection-${index}`}
															className={`${rowBg} ${index < Math.min(inspections.length, 4) - 1 ? 'border-b border-default-50' : ''}`}
														>
															<td role="cell" className="font-medium" style={{padding: '8px 8px 8px 0'}}>{displayName}</td>
															<td role="cell" className="whitespace-nowrap" style={{padding: '8px 8px 8px 0'}}>{dueDateMeter}</td>
															<td role="cell" className={`text-right whitespace-nowrap ${isDanger ? 'font-semibold' : 'opacity-70'}`} style={{padding: '8px 0'}}>
																{inspection.dueIn || '—'}
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								) : (
									<p className="text-default-400 text-sm py-2 italic">No inspections defined</p>
								)}
							</CardBody>
							<CardFooter className="pt-1">
								<Button
									variant="light"
									size="sm"
									className="w-full text-amber-600 hover:bg-amber-50"
									endContent={<ChevronRight className="w-4 h-4" />}
									onPress={handleManageInspections}
								>
									{accessGroups._loaded && !accessGroups.inspectionManage ? 'View Inspections' : 'Manage Inspections'}
								</Button>
							</CardFooter>
						</Card>
						)}

						{/* Discrepancies Card (discrepanciesView permission) */}
						{(accessGroups._loaded && accessGroups.discrepanciesView) && (
						<Card className={`border shadow-sm hover:shadow-md transition-shadow ${openDiscrepanciesCount > 0 ? 'border-rose-300 bg-gradient-to-br from-white to-rose-50/40' : 'border-default-200 bg-gradient-to-br from-white to-rose-50/20'}`}>
							<CardHeader className="bg-gradient-to-r from-rose-100/60 via-rose-50/40 to-transparent pb-2 border-b border-rose-100/30">
								<div className="flex items-center justify-between w-full">
									<div className="flex items-center gap-3">
										<div className={`p-1.5 rounded-lg ring-1 ${openDiscrepanciesCount > 0 ? 'bg-rose-500/20 ring-rose-500/30' : 'bg-rose-500/10 ring-rose-500/20'}`}>
											<AlertTriangle className={`w-4 h-4 ${openDiscrepanciesCount > 0 ? 'text-rose-600' : 'text-rose-500'}`} />
										</div>
										<span className="text-base font-semibold text-rose-900">Discrepancies</span>
									</div>
									{openDiscrepanciesCount > 0 && (
										<Chip size="sm" color="warning" variant="flat">
											{openDiscrepanciesCount} open
										</Chip>
									)}
								</div>
							</CardHeader>
							<CardBody className="pt-3 pb-2">
								{discrepanciesList.filter(d => d.status !== 'closed').length > 0 ? (
									<div className="space-y-1.5 max-h-28 overflow-y-auto">
										{discrepanciesList
											.filter(d => d.status !== 'closed')
											.slice(0, 3)
											.map((discrepancy, index) => (
												<div
													key={discrepancy.id || `discrepancy-${index}`}
													className="flex items-center gap-2 text-sm py-1.5 px-2 rounded bg-rose-50/50"
												>
													<span className={`w-2 h-2 rounded-full flex-shrink-0 ${
														discrepancy.status === 'open' ? 'bg-danger animate-pulse' : 'bg-warning'
													}`} />
													<span className="truncate text-xs font-medium text-rose-800">
														{discrepancy.name || discrepancy.title || '-'}
													</span>
												</div>
											))}
									</div>
								) : (
									<div className="flex items-center gap-2 py-2">
										<CheckCircle className="w-4 h-4 text-success" />
										<p className="text-success text-sm font-medium">No open discrepancies</p>
									</div>
								)}
							</CardBody>
							<CardFooter className="pt-1">
								<Button
									variant="light"
									size="sm"
									className="w-full text-rose-600 hover:bg-rose-50"
									endContent={<ChevronRight className="w-4 h-4" />}
									onPress={handleManageDiscrepancies}
								>
									View All
								</Button>
							</CardFooter>
						</Card>
						)}

						{/* Weight & Balance Card - Link */}
						<Card className="border border-default-200 shadow-sm bg-gradient-to-br from-white to-slate-50/30 hover:shadow-md transition-shadow">
							<CardHeader className="bg-gradient-to-r from-slate-100/80 via-slate-50/50 to-transparent pb-2 border-b border-slate-100/50">
								<div className="flex items-center gap-3">
									<div className="p-1.5 rounded-lg bg-slate-500/10 ring-1 ring-slate-500/20">
										<Scale className="w-4 h-4 text-slate-600" />
									</div>
									<span className="text-base font-semibold text-slate-800">Weight & Balance</span>
								</div>
							</CardHeader>
							<CardBody className="pt-3 pb-2">
								{aircraft.weightAndBalance?.basicEmptyWeight ? (
									<div className="space-y-2">
										<div className="flex justify-between items-center text-sm py-1.5 border-b border-default-100">
											<span className="text-default-500">Empty Weight</span>
											<span className="font-mono font-semibold text-slate-700">
												{aircraft.weightAndBalance.basicEmptyWeight.weight} {aircraft.weightAndBalance.weightUnit || 'lbs'}
											</span>
										</div>
										{aircraft.weightAndBalance.maxTakeoffWeight && (
											<div className="flex justify-between items-center text-sm py-1.5">
												<span className="text-default-500">Max Takeoff</span>
												<span className="font-mono font-semibold text-slate-700">
													{aircraft.weightAndBalance.maxTakeoffWeight} {aircraft.weightAndBalance.weightUnit || 'lbs'}
												</span>
											</div>
										)}
									</div>
								) : (
									<p className="text-default-400 text-sm py-2 italic">Not configured</p>
								)}
							</CardBody>
							<CardFooter className="pt-1">
								<Button
									variant="light"
									size="sm"
									className="w-full text-slate-600 hover:bg-slate-50"
									endContent={<ChevronRight className="w-4 h-4" />}
									onPress={handleManageWeightBalance}
								>
									{(accessGroups._loaded && accessGroups.resourcesManage) ? 'Configure' : 'View'}
								</Button>
							</CardFooter>
						</Card>
					</div>
				</div>
			</>
		);
	};

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-8">
			{/* Breadcrumbs */}
			<div className="mb-6">
				<HeroUIBreadcrumb
					currentPage={aircraft?.name || 'View Aircraft'}
					customItems={breadcrumbCustomItems}
				/>
			</div>

			{/* Page Content */}
			{loading && renderSkeleton()}
			{error && renderError()}
			{!loading && !error && renderContent()}

			{/* Delete Confirmation Modal */}
			<Modal scrollBehavior="inside" isOpen={isDeleteOpen} onClose={onDeleteClose}>
				<ModalContent>
					{onClose => (
						<>
							<ModalHeader className="flex flex-col gap-1">Delete Aircraft</ModalHeader>
							<ModalBody>
								<p>
									Are you sure you want to delete aircraft <strong>{aircraft?.name}</strong>? This
									action cannot be undone.
								</p>
							</ModalBody>
							<ModalFooter>
								<Button color="default" variant="light" onPress={onClose}>
									Cancel
								</Button>
								<Button color="danger" onPress={handleDelete}>
									Delete Aircraft
								</Button>
							</ModalFooter>
						</>
					)}
				</ModalContent>
			</Modal>

			{/* Model Search Modal */}
			<Modal isOpen={isModelSearchOpen} onClose={onModelSearchClose} size="2xl" scrollBehavior="inside">
				<ModalContent>
					{onClose => (
						<>
							<ModalHeader className="flex flex-col gap-1">Change Aircraft Model</ModalHeader>
							<ModalBody>
								<div className="space-y-4">
									<AircraftModelSearch
										onSelect={handleSelectModel}
										showHistory
										size="sm"
										placeholder="Search by model or manufacturer name..."
										maxHeight={320}
									/>
									<p className="text-xs text-default-400">
										Don't see your aircraft model? Contact support to add it.
									</p>
								</div>
							</ModalBody>
							<ModalFooter>
								<Button color="default" variant="light" onPress={onClose}>
									Cancel
								</Button>
							</ModalFooter>
						</>
					)}
				</ModalContent>
			</Modal>
		</div>
	);
}

function ViewAircraftPage() {
	return (
		<UnsavedChangesGuard>
			<ViewAircraftPageContent />
		</UnsavedChangesGuard>
	);
}

export default ViewAircraftPage;
